import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { pathgen } from 'object-path-generator';
import { TableList, TableListActions, Typography, useDevice } from 'seagull';
import { Edit } from 'seagull/icons';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useDraftRowMenuItems } from '~/components/DraftActions/DraftRowMenuItems';
import { useDraftRowActions } from '~/components/DraftActions/useDraftRowActions';
import { routes } from '~/constants';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { DraftStatusType, timeModel } from '~/models';
import { timeTextsPath } from '~/models/time/selectors';
import styles from './DraftsDashboard.module.scss';
import { getUICompanyName } from './DraftsDashboard.utils';
import { ScheduledTooltip } from './ScheduledTooltip';
export const DraftsDashboardRowTestIds = pathgen('DraftsDashboardRowTestIds');
export default function DraftsDashboardRow(props) {
    var _draft_lastModifiedBy, _draft_lastModifiedBy1;
    const { draft, index, refresh } = props;
    const { t: rowTexts } = useLocaleKeys('pages.draftsDashboard.row');
    const { t: editTableTexts } = useLocaleKeys('pages.draftsDashboard.editTable');
    const { t: timeTexts } = useLocaleKeys(timeTextsPath);
    const { logBI } = useStoreActions();
    const { isMobileDevice } = useDevice();
    const history = useHistory();
    const companyName = getUICompanyName(draft, editTableTexts.anchorSample());
    const title = draft.title;
    const isScheduledProposal = draft.status.type === DraftStatusType.Scheduled;
    const { onEdit, onPreview, onSendNow, onCancelScheduling, onDuplicate, onArchive, onDelete } = useDraftRowActions({
        draftId: draft.id,
        isSample: draft.isSample,
        isScheduled: isScheduledProposal,
        refresh
    });
    const menuItems = useDraftRowMenuItems({
        index,
        isScheduled: isScheduledProposal,
        onPreview,
        onSendNow,
        onCancelScheduling,
        onDuplicate,
        onArchive,
        onDelete
    });
    function renderMobileCompanyNameCell() {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                companyName && /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "draft-company-name-".concat(index),
                    children: companyName
                }),
                title && /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    skin: "secondary",
                    testId: "draft-name-".concat(index),
                    style: {
                        paddingBlock: 4
                    },
                    children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                        text: title,
                        mode: "text"
                    })
                })
            ]
        });
    }
    function renderDesktopCompanyNameCell() {
        return companyName ? /*#__PURE__*/ _jsxs("div", {
            className: styles.scheduledTooltip,
            children: [
                /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "draft-company-name-".concat(index),
                    children: [
                        companyName,
                        title && /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            variant: "paragraph2",
                            skin: "secondary",
                            testId: "draft-name-".concat(index),
                            children: [
                                ' ',
                                "(",
                                /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                    text: title,
                                    mode: "text"
                                }),
                                ")"
                            ]
                        })
                    ]
                }),
                draft.status.type === DraftStatusType.Scheduled && /*#__PURE__*/ _jsx(ScheduledTooltip, {
                    index: index,
                    scheduleDate: draft.status.schedule.time
                })
            ]
        }) : /*#__PURE__*/ _jsxs(Typography, {
            as: "span",
            variant: "paragraph2",
            skin: "secondary",
            testId: "draft-name-".concat(index),
            children: [
                "(",
                /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                    text: title || rowTexts.unnamedDraft(),
                    mode: "text"
                }),
                ")"
            ]
        });
    }
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: draft.id,
        testId: "drafts-row",
        "data-heapid": "draft",
        as: 'button',
        to: routes.draft(draft.id),
        onClick: ()=>{
            logBI(10352);
            history.push(routes.draft(draft.id));
        },
        items: [
            {
                value: isMobileDevice ? renderMobileCompanyNameCell() : renderDesktopCompanyNameCell()
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    children: "".concat((_draft_lastModifiedBy = draft.lastModifiedBy) === null || _draft_lastModifiedBy === void 0 ? void 0 : _draft_lastModifiedBy.firstName, " ").concat((_draft_lastModifiedBy1 = draft.lastModifiedBy) === null || _draft_lastModifiedBy1 === void 0 ? void 0 : _draft_lastModifiedBy1.lastName)
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: timeModel.formatTimeAgo(draft.lastModifiedOn, timeTexts)
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            buttonText: rowTexts.edit(),
            buttonIcon: /*#__PURE__*/ _jsx(Edit, {}),
            testId: "table-list-actions-".concat(index),
            btnHeapId: "draft-view-btn",
            menuHeapId: "draft-menu-btn",
            onClickButton: onEdit,
            mobile: {
                title: companyName,
                subtitle: title
            },
            children: menuItems
        })
    }, draft.id);
}
